import { WebPubSubClient, OnConnectedArgs } from '@azure/web-pubsub-client';
import { API_URL } from '@env';
import { isArray } from 'lodash';
import { Observable, of, Subject } from 'rxjs';
import { filter, map, switchMap, takeUntil } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { FsDataDogRumService } from '@lib/data-dog-rum';

import {
  AZURE_PUB_SUB_API_ENDPOINTS,
  AzureWebPubSubEvents,
  EventTypes,
} from './azure-pub-sub.constants';
import { AzureAnalyticsActionMap, FsAzurePubSubEvent } from './azure-pub-sub.types';
import { HeartbeatManager } from './utils/heartbeatManager';

interface MessageMetrics {
  messageCount: number;
  startTime: number;
  messages: Array<{
    id: string;
    receivedTimestamp: number;
    group: string;
  }>;
}

const DEFAULT_METRIC_INTERVAL = 120000; // 2 Minutes

@Injectable({ providedIn: 'root' })
export class FsAzurePubSubService {
  private _client: WebPubSubClient | null = null;
  private _connectionId: string | null = null;
  private _organizationId: string | null = null;
  private _isActive = false;
  private _messageMetrics: MessageMetrics = {
    messageCount: 0,
    startTime: Date.now(),
    messages: [],
  };

  private _metricsTimer: any = null;
  private _heartbeatManager?: HeartbeatManager;

  private _connected$ = new Subject<string>();
  private _disconnected$ = new Subject<void>();
  private _messages$ = new Subject<FsAzurePubSubEvent>();
  private _destroy$ = new Subject<void>();

  private _groupMessages$ = new Map<string, Subject<FsAzurePubSubEvent>>();
  private _connectedHandler?: (e: OnConnectedArgs) => void;

  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _datadogService: FsDataDogRumService,
  ) {
    this._handleGroupMessage = this._handleGroupMessage.bind(this);
    this.initAzurePubSubClient = this.initAzurePubSubClient.bind(this);
  }

  private _onConnected = (e: OnConnectedArgs): void => {
    this._connectionId = e.connectionId;
    this._connected$.next(e.connectionId);
    this.addAnalytics('azure_pubsub_client_connection_success', {
      connectionId: this._connectionId,
      organizationId: this._organizationId || 'unknown',
    });
    this._startMessageMetricsTracking();
    this._heartbeatManager = new HeartbeatManager(() =>
      this._client
        .sendEvent(EventTypes.Heartbeat, { connectionId: this._connectionId }, 'json')
        .then(() => {}),
    );
    this._heartbeatManager.start();
  };

  private _onDisconnected = (): void => {
    this.addAnalytics('azure_pubsub_client_disconnect_success', {
      connectionId: this._connectionId,
      organizationId: this._organizationId || 'unknown',
    });
    // Send final metrics before disconnecting
    this._sendMessageMetrics();
    if (this._metricsTimer) {
      clearInterval(this._metricsTimer);
      this._metricsTimer = null;
    }
    this._connectionId = null;
    this._disconnected$.next();
  };

  public async initAzurePubSubClient(
    connectionUrl: string,
    organizationId?: string,
  ): Promise<void> {
    if (this._client) {
      await this.destroy();
    }

    this._organizationId = organizationId || null;
    this._isActive = true;

    return new Promise((resolve, reject) => {
      try {
        // Use credential pattern so SDK retries get fresh URLs
        const credential = {
          getClientAccessUrl: async () =>
            localStorage.getItem('azureWebpubsubUrl') || connectionUrl,
        };

        // Use credential approach for dynamic URL resolution
        this._client = new WebPubSubClient(credential);

        this._connectedHandler = (e: OnConnectedArgs) => {
          this._onConnected(e);
          resolve();
        };
        this._client.on(AzureWebPubSubEvents.Connected, this._connectedHandler);
        this._client.on(AzureWebPubSubEvents.Disconnected, this._onDisconnected);
        this._client.on(AzureWebPubSubEvents.GroupMessage, this._handleGroupMessage);

        this._client.start().catch((error) => {
          this.addAnalytics('azure_pubsub_client_connection_failure', {
            error: error.message || JSON.stringify(error),
            organizationId: this._organizationId || 'unknown',
          });
          reject(
            new Error(`WebSocket connection failed: ${error.message || JSON.stringify(error)}`),
          );
        });
      } catch (error) {
        this.addAnalytics('azure_pubsub_client_connection_failure', {
          error: error.message || JSON.stringify(error),
          organizationId: this._organizationId || 'unknown',
        });
        reject(
          new Error(`Client initialization failed: ${error.message || JSON.stringify(error)}`),
        );
      }
    });
  }

  public sendEvent = async (event: EventTypes, channelName: string): Promise<void> => {
    if (!this._isActive || !this._client) {
      this.addAnalytics('azure_pubsub_client_not_initialized', {
        organizationId: this._organizationId || 'unknown',
      });
      throw new Error('Client not initialized');
    }

    try {
      if (event === EventTypes.LeaveChannel) {
        const groupSubject = this._groupMessages$.get(channelName);
        if (groupSubject) {
          groupSubject.complete();
          this._groupMessages$.delete(channelName);
        }
      }

      const payload =
        event === EventTypes.Heartbeat
          ? { connectionId: this._connectionId }
          : { channels: [channelName] };

      await this._client?.sendEvent(event, payload, 'json').then((res) =>
        this.addAnalytics('azure_pubsub_send_event_success', {
          channelName: [channelName],
          event,
          connectionId: this._connectionId,
          organizationId: this._organizationId || 'unknown',
        }),
      );
    } catch (error) {
      this.addAnalytics('azure_pubsub_send_event_failure', {
        channelName: [channelName],
        event,
        connectionId: this._connectionId,
        organizationId: this._organizationId || 'unknown',
        error,
      });
      throw error;
    }
  };

  public get connectionId(): string | null {
    return this._connectionId;
  }

  public onConnected(): Observable<string> {
    return this._connected$.asObservable();
  }

  public onDisconnected(): Observable<void> {
    return this._disconnected$.asObservable();
  }

  public onReconnect(): Observable<void> {
    return this._disconnected$.pipe(
      switchMap(() => this._connected$),
      map(() => undefined),
    );
  }

  public onMessage<T = any>(groupName: string, filterFn?: (msg: T) => boolean): Observable<T> {
    if (!this._groupMessages$.has(groupName)) {
      this._groupMessages$.set(groupName, new Subject<FsAzurePubSubEvent>());
    }

    return this._groupMessages$
      .get(groupName)!
      .asObservable()
      .pipe(
        filter((event): event is FsAzurePubSubEvent<T> => {
          if (!filterFn) return true;
          return filterFn(event.message);
        }),
        takeUntil(this._destroy$),
        map((event) => event.message),
      );
  }

  public addAnalytics<T extends keyof AzureAnalyticsActionMap>(
    message: T,
    data?: AzureAnalyticsActionMap[T],
  ): void {
    this._datadogService.addAction(message, data);
  }

  private _cleanupSubjects(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _cleanupConnectionSubjects(): void {
    this._connected$.complete();
    this._disconnected$.complete();
    this._messages$.complete();
  }

  private _resetSubjects(): void {
    this._destroy$ = new Subject<void>();
    this._connected$ = new Subject<string>();
    this._disconnected$ = new Subject<void>();
    this._messages$ = new Subject<FsAzurePubSubEvent>();
  }

  public async destroy(): Promise<void> {
    this._isActive = false; // session guard
    this._heartbeatManager?.stop();
    this._heartbeatManager = undefined;

    this._sendMessageMetrics();
    if (this._metricsTimer) {
      clearInterval(this._metricsTimer);
      this._metricsTimer = null;
    }

    this._groupMessages$.forEach((subject) => {
      subject.complete();
    });
    this._groupMessages$.clear();

    if (this._client) {
      await this._client.stop(); // Stop autoreconnect & close WebSocket
      this._client = null;
      this._connectionId = null;
    }

    // Clean up internal subjects
    this._cleanupSubjects();
    this._cleanupConnectionSubjects();
  }

  public async reset(): Promise<void> {
    await this.destroy();
    this._resetSubjects();
  }

  private _handleGroupMessage(msg: any): void {
    let parsedData: any;
    try {
      parsedData =
        typeof msg.message?.data === 'string' ? JSON.parse(msg.message.data) : msg.message.data;
    } catch (e) {
      parsedData = msg.message?.data;
    }

    const groupName = msg.message?.group;
    if (!groupName) {
      return;
    }

    if (!this._groupMessages$.has(groupName)) {
      this._groupMessages$.set(groupName, new Subject<FsAzurePubSubEvent>());
    }

    const groupSubject = this._groupMessages$.get(groupName)!;

    if (isArray(parsedData)) {
      parsedData.forEach((item) => {
        this._trackMessageDelivery(item, groupName);
        groupSubject.next({
          connectionId: this._connectionId,
          message: item,
          group: groupName,
        });
      });
    } else {
      this._trackMessageDelivery(parsedData, groupName);
      groupSubject.next({
        connectionId: this._connectionId,
        message: parsedData,
        group: groupName,
      });
    }
  }

  private _registerConnection(eventType: AzureWebPubSubEvents): Observable<any> {
    if (!this._connectionId) return of(null);

    const body = {
      connectionId: this._connectionId,
      timestamp: new Date().toISOString(),
      eventType,
    };

    return this._httpClient.post(
      `${API_URL}${AZURE_PUB_SUB_API_ENDPOINTS.CLIENT_CONNECTIONS}`,
      body,
      { withCredentials: true },
    );
  }

  private _trackMessageDelivery(message: any, group: string): void {
    const receivedTimestamp = Date.now();
    this._messageMetrics.messageCount++;

    this._messageMetrics.messages.push({
      id: message.id || message.data.id || 'unknown',
      receivedTimestamp,
      group,
    });
  }

  private _startMessageMetricsTracking(intervalMs: number = DEFAULT_METRIC_INTERVAL): void {
    this._resetMessageMetrics();
    this._metricsTimer = setInterval(() => {
      this._sendMessageMetrics();
    }, intervalMs);
  }

  private _resetMessageMetrics(): void {
    this._messageMetrics = {
      messageCount: 0,
      startTime: Date.now(),
      messages: [],
    };
  }

  private _sendMessageMetrics(): void {
    const { messageCount, startTime, messages } = this._messageMetrics;
    const duration = Date.now() - startTime;

    if (messageCount > 0) {
      this.addAnalytics('azure_pubsub_message_metrics', {
        messageCount,
        duration,
        connectionId: this._connectionId,
        organizationId: this._organizationId || 'unknown',
        messages: messages.map((msg) => ({
          id: msg.id,
          receivedTimestamp: msg.receivedTimestamp,
          group: msg.group,
        })),
      });
    }

    this._resetMessageMetrics();
  }
}
